import React, { useEffect, useState } from "react";
import {
  Users, TrendingUp, RefreshCw, Loader2, Sparkles, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { projectLead, errorMessage } from "../../lib/api";

const STATE_STYLE = {
  on_track:    { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "On track" },
  complete:    { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Complete" },
  behind:      { color: "text-amber-700 bg-amber-50 border-amber-200",       label: "Behind" },
  not_started: { color: "text-red-700 bg-red-50 border-red-200",             label: "Not started" },
};

const healthStyle = (phrase) => {
  const p = (phrase || "").toLowerCase();
  if (p.includes("ready") || p.includes("on track")) return "bg-emerald-50 border-emerald-200 text-emerald-800";
  if (p.includes("not started")) return "bg-red-50 border-red-200 text-red-800";
  return "bg-amber-50 border-amber-200 text-amber-800";
};

/** AI Project Lead — owner-facing group insight card. Behaves like a real
 *  project manager: leads with what still needs doing, shows an estimated
 *  grade + top 3 improvements, and flags member risk and content overlap. */
export default function ProjectLeadCard({ group }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    (force ? setRefreshing : setLoading)(true);
    try {
      const res = await projectLead(group.id, force);
      setData(res);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [group.id]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-indigo-200 bg-white p-5 flex items-center gap-3" data-testid="project-lead-loading">
        <Loader2 className="animate-spin text-indigo-600" size={18} />
        <span className="text-sm text-zinc-700">Your Project Lead is looking at the group…</span>
      </div>
    );
  }
  if (!data) return null;

  const g = data.estimated_grade || {};

  return (
    <div className="rounded-2xl border border-indigo-200 bg-white shadow-sm overflow-hidden" data-testid="project-lead-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100 px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-sm">
          <Sparkles size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-indigo-700">AI Project Lead</p>
            {data.cached && <span className="text-[10px] text-zinc-400">cached</span>}
          </div>
          <p className="font-display text-lg text-zinc-900 leading-tight">{data.one_line_summary || data.health_phrase}</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="project-lead-refresh"
          className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 border border-indigo-200 rounded-md px-2 py-1"
        >
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="project-lead-toggle"
          className="text-xs text-zinc-500 hover:text-zinc-900 p-1"
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {expanded && (
        <div className="px-5 py-4 space-y-5">
          {/* Health + estimated grade */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className={`rounded-xl border px-4 py-3 ${healthStyle(data.health_phrase)}`} data-testid="project-lead-health">
              <p className="text-[10px] uppercase tracking-widest font-semibold opacity-70">Assignment Health</p>
              <p className="font-display text-lg font-semibold">{data.health_phrase}</p>
              <p className="text-xs mt-1 opacity-80">{data.sections_ready} of {data.sections_total} sections ready</p>
            </div>
            {g.most_likely > 0 && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3" data-testid="project-lead-grade">
                <p className="text-[10px] uppercase tracking-widest font-semibold text-indigo-700">Estimated Group Grade</p>
                <p className="font-display text-lg font-semibold text-indigo-900 tabular-nums">
                  {g.range_low}–{g.range_high}%<span className="text-sm text-indigo-700 ml-2">most likely {g.most_likely}%</span>
                </p>
                <p className="text-xs text-indigo-800 mt-0.5">{g.band}</p>
              </div>
            )}
          </div>

          {/* Still needed */}
          {(data.still_needed || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2">Still needed</p>
              <ul className="space-y-1.5" data-testid="project-lead-still-needed">
                {data.still_needed.map((n, i) => (
                  <li key={i} className="text-sm text-zinc-800 flex items-start gap-2" data-testid={`project-lead-need-${i}`}>
                    <AlertCircle size={13} className="text-amber-500 mt-0.5 shrink-0" /> {n}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Top improvements */}
          {(data.top_improvements || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <TrendingUp size={13} /> Top 3 improvements to lift the grade
              </p>
              <div className="space-y-2" data-testid="project-lead-improvements">
                {data.top_improvements.slice(0, 3).map((imp, i) => (
                  <div key={i} className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2" data-testid={`project-lead-improvement-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-zinc-900">{imp.title}</p>
                      {imp.expected_grade_lift && (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 tabular-nums shrink-0">
                          {imp.expected_grade_lift}
                        </span>
                      )}
                    </div>
                    {imp.why && <p className="text-xs text-zinc-600 mt-0.5">{imp.why}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Member status */}
          {(data.member_status || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Users size={13} /> Members
              </p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="project-lead-members">
                {data.member_status.map((m, i) => {
                  const st = STATE_STYLE[m.state] || STATE_STYLE.behind;
                  return (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white px-3 py-2 flex items-center gap-3" data-testid={`project-lead-member-${i}`}>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full border px-2 py-0.5 whitespace-nowrap ${st.color}`}>
                        {st.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">{m.name}</p>
                        {m.note && <p className="text-xs text-zinc-500 truncate">{m.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overlaps + sections drifting from brief */}
          {((data.overlaps || []).length > 0 || (data.sections_not_matching_brief || []).length > 0) && (
            <div className="grid md:grid-cols-2 gap-3">
              {(data.overlaps || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-1">Overlaps to resolve</p>
                  <ul className="space-y-1" data-testid="project-lead-overlaps">
                    {data.overlaps.map((o, i) => (
                      <li key={i} className="text-xs text-zinc-700"><span className="text-amber-500 mr-1">•</span>{o}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(data.sections_not_matching_brief || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-1">Off-brief content</p>
                  <ul className="space-y-1" data-testid="project-lead-off-brief">
                    {data.sections_not_matching_brief.map((s, i) => (
                      <li key={i} className="text-xs text-zinc-700"><span className="text-amber-500 mr-1">•</span>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Clean-slate confirmation */}
          {(data.still_needed || []).length === 0 && (data.top_improvements || []).length === 0 && (
            <div className="text-sm text-emerald-800 flex items-center gap-2" data-testid="project-lead-all-good">
              <CheckCircle2 size={16} /> Nothing outstanding — this group is submission-ready.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
